<template>
  <div class="exam-config-editor" v-loading="loading">
    <!-- Warning if no questions available -->
    <el-alert
      v-if="totalAvailableQuestions === 0"
      title="No Questions Available"
      type="warning"
      description="The collections in this bundle don't have any test bank questions yet. Add questions to the collections' test banks before enabling examinations."
      show-icon
      :closable="false"
      style="margin-bottom: 20px;"
    />

    <!-- Info about question source -->
    <el-alert
      v-else
      type="info"
      :closable="false"
      style="margin-bottom: 20px;"
    >
      <span>Questions are drawn from all collections' test banks in this bundle. <strong>{{ totalAvailableQuestions }}</strong> questions available across {{ collectionCount }} collection(s).</span>
    </el-alert>

    <el-form
      ref="configForm"
      :model="config"
      label-position="top"
    >
      <!-- Master Toggle -->
      <div class="section-card">
        <div class="section-header">
          <h3>Examination Status</h3>
          <el-switch
            v-model="config.exam_enabled"
            :disabled="totalAvailableQuestions === 0"
            active-text="Enabled"
            inactive-text="Disabled"
            @change="handleSave"
          />
        </div>
        <p class="section-hint">Enable examinations for this bundle. Students can take exams <strong>only on the mobile app</strong> for security.</p>
      </div>

      <template v-if="config.exam_enabled">
        <!-- Basic Settings -->
        <div class="section-card">
          <h3>Basic Settings</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="Exam Title">
                <el-input v-model="config.exam_title" placeholder="e.g., Bundle Final Examination" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="Number of Questions">
                <el-input-number
                  v-model="config.exam_question_count"
                  :min="0"
                  :max="totalAvailableQuestions"
                  style="width: 100%;"
                />
                <span class="field-hint">{{ totalAvailableQuestions }} available from all collections. 0 = use all.</span>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="Instructions for Students">
            <el-input
              type="textarea"
              v-model="config.exam_description"
              placeholder="Enter exam instructions..."
              :rows="3"
            />
          </el-form-item>
        </div>

        <!-- Question Selection -->
        <div class="section-card">
          <h3>Question Selection</h3>
          <el-form-item label="Selection Mode">
            <el-radio-group v-model="config.exam_question_selection">
              <el-radio-button label="random">Random</el-radio-button>
              <el-radio-button label="sequential">Sequential</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <p class="section-hint" v-if="config.exam_question_selection === 'random'">
            Questions will be randomly selected and shuffled from all collections' test banks.
          </p>
          <p class="section-hint" v-if="config.exam_question_selection === 'sequential'">
            Questions will be presented in order, grouped by collection.
          </p>
        </div>

        <!-- Time Settings -->
        <div class="section-card">
          <h3>Time Settings</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item>
                <el-checkbox v-model="config.exam_time_limit_enabled">Enable Time Limit</el-checkbox>
              </el-form-item>
              <el-form-item v-if="config.exam_time_limit_enabled" label="Time Limit (minutes)">
                <el-input-number v-model="config.exam_time_limit_minutes" :min="1" :max="480" style="width: 100%;" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <p class="section-hint" style="margin-top: 40px;" v-if="config.exam_time_limit_enabled">
                Exam will auto-submit when time runs out.
              </p>
            </el-col>
          </el-row>
        </div>

        <!-- Attempt & Scoring Settings -->
        <div class="section-card">
          <h3>Attempt & Scoring Settings</h3>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="Max Attempts">
                <el-input-number v-model="config.exam_max_attempts" :min="0" style="width: 100%;" />
                <span class="field-hint">0 = Unlimited</span>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Cooldown (hours)">
                <el-input-number v-model="config.exam_cooldown_hours" :min="0" style="width: 100%;" />
                <span class="field-hint">Wait time between attempts</span>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="Passing Score (%)">
                <el-input-number v-model="config.exam_passing_score" :min="0" :max="100" style="width: 100%;" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item>
                <el-checkbox v-model="config.exam_show_score">Show Score to Student</el-checkbox>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item>
                <el-checkbox v-model="config.exam_show_answers">Allow Answer Review</el-checkbox>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- Proctoring Settings -->
        <div class="section-card">
          <h3>Proctoring Settings</h3>
          <el-alert
            type="info"
            :closable="false"
            style="margin-bottom: 16px;"
          >
            <strong>Mobile-only exams:</strong> All exams are taken exclusively on the mobile app for security (like Duolingo). Proctoring monitors student behavior during the exam.
          </el-alert>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item>
                <el-checkbox v-model="config.exam_proctoring_enabled">Enable Proctoring</el-checkbox>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item v-if="config.exam_proctoring_enabled">
                <el-checkbox v-model="config.exam_require_camera">Require Camera</el-checkbox>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item v-if="config.exam_proctoring_enabled">
                <el-checkbox v-model="config.exam_detect_app_switch">Detect App Switching</el-checkbox>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20" v-if="config.exam_proctoring_enabled">
            <el-col :span="12" v-if="config.exam_detect_app_switch">
              <el-form-item label="Max App Switches Allowed">
                <el-input-number v-model="config.exam_max_app_switches" :min="1" :max="10" style="width: 100%;" />
                <span class="field-hint">Auto-submit after exceeding limit</span>
              </el-form-item>
            </el-col>
            <el-col :span="12" v-if="config.exam_require_camera">
              <el-form-item label="Photo Capture Interval (seconds)">
                <el-input-number v-model="config.exam_capture_interval" :min="30" :max="300" style="width: 100%;" />
                <span class="field-hint">Capture student photos during exam</span>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- Certificate Settings -->
        <div class="section-card">
          <h3>Certificate Settings</h3>
          <el-form-item>
            <el-checkbox v-model="config.exam_certificate_enabled">Issue Certificate on Pass</el-checkbox>
          </el-form-item>
          <template v-if="config.exam_certificate_enabled">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="Certificate Title">
                  <el-input v-model="config.exam_certificate_title" placeholder="Leave blank to use bundle title" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Validity Period (days)">
                  <el-input-number v-model="config.exam_certificate_expiry_days" :min="0" style="width: 100%;" />
                  <span class="field-hint">0 = Never expires</span>
                </el-form-item>
              </el-col>
            </el-row>
            <el-form-item>
              <el-checkbox v-model="config.exam_certificate_show_score">Show Score on Certificate</el-checkbox>
            </el-form-item>
            <p class="section-hint">
              Certificates include a unique verification code that can be validated at knowit.today/verify
            </p>
          </template>
        </div>

        <!-- Scheduling (Optional) -->
        <div class="section-card">
          <h3>Exam Scheduling (Optional)</h3>
          <el-form-item>
            <el-checkbox v-model="config.exam_scheduled">Enable Scheduled Exam</el-checkbox>
          </el-form-item>
          <el-row :gutter="20" v-if="config.exam_scheduled">
            <el-col :span="12">
              <el-form-item label="Start Time">
                <el-date-picker
                  v-model="examStartDate"
                  type="datetime"
                  placeholder="Select start time"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="End Time">
                <el-date-picker
                  v-model="examEndDate"
                  type="datetime"
                  placeholder="Select end time"
                  style="width: 100%;"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- Save Button -->
        <div class="save-section">
          <el-button type="primary" size="medium" @click="handleSave" :loading="saving">
            Save Configuration
          </el-button>
        </div>
      </template>
    </el-form>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'BundleExamConfigurationEditor',
  props: {
    bundleId: {
      type: String,
      required: true
    },
    collectionCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      examStartDate: null,
      examEndDate: null,
      totalAvailableQuestions: 0,
      config: {
        exam_enabled: false,
        exam_title: '',
        exam_description: '',
        exam_question_count: 10,
        exam_question_selection: 'random',
        difficulty_distribution: {
          easy: 30,
          medium: 50,
          hard: 20
        },
        exam_time_limit_enabled: true,
        exam_time_limit_minutes: 30,
        exam_max_attempts: 3,
        exam_cooldown_hours: 24,
        exam_show_answers: true,
        exam_show_score: true,
        exam_passing_score: 70,
        exam_proctoring_enabled: false,
        exam_require_camera: false,
        exam_detect_app_switch: true,
        exam_max_app_switches: 3,
        exam_capture_interval: 60,
        exam_certificate_enabled: true,
        exam_certificate_title: '',
        exam_certificate_show_score: true,
        exam_certificate_expiry_days: 0,
        exam_scheduled: false,
        exam_start_time: null,
        exam_end_time: null
      }
    }
  },
  watch: {
    bundleId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchConfig()
        }
      }
    },
    examStartDate(val) {
      this.config.exam_start_time = val ? Math.floor(val.getTime() / 1000) : null
    },
    examEndDate(val) {
      this.config.exam_end_time = val ? Math.floor(val.getTime() / 1000) : null
    }
  },
  methods: {
    async fetchConfig() {
      this.loading = true
      try {
        const response = await postRequest('getBundleExamConfig', {
          bundle_id: this.bundleId,
          user_id: this.$store.state.user_id
        })

        if (response.data.code === 0 && response.data.data) {
          const data = response.data.data

          // Update stats - total questions from all collections' test banks
          this.totalAvailableQuestions = data.total_available_questions || 0

          // Map backend fields to component config
          const configFields = [
            'exam_enabled', 'exam_title', 'exam_description',
            'exam_question_count', 'exam_question_selection',
            'exam_time_limit_enabled', 'exam_time_limit_minutes',
            'exam_passing_score', 'exam_show_score', 'exam_show_answers',
            'exam_max_attempts', 'exam_cooldown_hours',
            'exam_scheduled', 'exam_start_time', 'exam_end_time',
            'exam_proctoring_enabled', 'exam_require_camera',
            'exam_detect_app_switch', 'exam_max_app_switches',
            'exam_capture_interval', 'exam_certificate_enabled',
            'exam_certificate_title', 'exam_certificate_show_score',
            'exam_certificate_expiry_days'
          ]

          configFields.forEach(key => {
            if (data[key] !== undefined) {
              this.config[key] = data[key]
            }
          })

          // Handle dates
          if (data.exam_start_time) {
            this.examStartDate = new Date(data.exam_start_time * 1000)
          }
          if (data.exam_end_time) {
            this.examEndDate = new Date(data.exam_end_time * 1000)
          }
        }
      } catch (error) {
        console.error('Error fetching bundle exam config:', error)
      } finally {
        this.loading = false
      }
    },

    async handleSave() {
      this.saving = true
      try {
        const response = await postRequest('updateBundleExamConfig', {
          bundle_id: this.bundleId,
          user_id: this.$store.state.user_id,
          ...this.config
        })

        if (response.data.code === 0) {
          this.$message.success('Configuration saved')
        } else {
          this.$message.error(response.data.msg || 'Failed to save')
        }
      } catch (error) {
        console.error('Error saving bundle exam config:', error)
        this.$message.error('Failed to save configuration')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.exam-config-editor {
  padding: 20px;
  max-width: 900px;
}

.section-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-card h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #303133;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-hint {
  color: #909399;
  font-size: 13px;
  margin-top: 8px;
}

.field-hint {
  display: block;
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}

.save-section {
  display: flex;
  justify-content: flex-end;
  padding: 20px 0;
}
</style>
